import { useCallback, useEffect, useState } from "react";
import CashEntryServices from "../../../services/CashEntryServices";

export const readCashData = (response) => {
  const body = response?.data?.status ? response.data : response;
  return body?.data;
};

export const cashId = (value) =>
  String(value?._id || value || "");

export const cashError = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Request failed.";

export const cashRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem("userDetails"));

    return String(
      user?.data?.user?.role || ""
    ).toLowerCase();
  } catch {
    return "";
  }
};

export const cashMoney = (value) =>
  value === null || value === undefined
    ? "—"
    : Number(value).toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

export const cashDate = (value) =>
  value && Number.isFinite(new Date(value).getTime())
    ? new Date(value).toLocaleString("en-GB", {
        timeZone: "Asia/Kolkata"
      })
    : "—";

export const cashLocalDateTime = (value) => {
  const time = new Date(value).getTime();

  return Number.isFinite(time)
    ? new Date(time + 330 * 60000)
        .toISOString()
        .slice(0, 16)
    : "";
};

export const cashFileUrl = (name) => {
  const base = ["localhost", "127.0.0.1"].includes(
    window.location.hostname
  )
    ? "http://localhost:3001"
    : "https://indigoinfra.in";

  return `${base}/img/cash-entries/${encodeURIComponent(name)}`;
};

export const activeCashDates = (windowData) =>
  (windowData?.approvedDates || []).filter(
    (grant) =>
      new Date(grant.expiresAt).getTime() > Date.now()
  );

export const isAllowedCashDate = (
  value,
  windowData,
  original = ""
) => {
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value || "")
  ) {
    return false;
  }

  const parsed = new Date(`${value}:00+05:30`);

  if (
    !Number.isFinite(parsed.getTime()) ||
    cashLocalDateTime(parsed) !== value
  ) {
    return false;
  }

  // Unchanged original amendment date remains allowed.
  if (original && value === original) return true;

  if (!windowData) return false;

  if (
    value >= windowData.min &&
    value <= windowData.max
  ) {
    return true;
  }

  return activeCashDates(windowData).some(
    (grant) =>
      value >= grant.min &&
      value <= grant.max
  );
};

export const validateCashFiles = (
  files,
  retained = []
) => {
  const allowed = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/pdf"
  ];

  if (
    files.some(
      (file) =>
        !(file instanceof File) ||
        !allowed.includes(file.type) ||
        file.size <= 0
    )
  ) {
    return "Select valid JPG, PNG, WEBP images or a PDF.";
  }

  if (
    files.some(
      (file) => file.size > 5 * 1024 * 1024
    )
  ) {
    return "Each attachment must be 5 MB or smaller.";
  }

  const count = files.length + retained.length;

  if (count > 2) {
    return "Maximum two images or one PDF, including retained files.";
  }

  const hasPdf =
    retained.some((name) => /\.pdf$/i.test(name)) ||
    files.some((file) => file.type === "application/pdf");

  if (hasPdf && count !== 1) {
    return "Keep one PDF only. Remove other attachments first.";
  }

  return "";
};

export const useCashEntryDateWindow = (passbookId) => {
  const [refresh, setRefresh] = useState(0);

  const [state, setState] = useState({
    key: "",
    dateWindow: null,
    dateWindowError: ""
  });

  const key = cashId(passbookId);

  const reload = useCallback(() => {
    setRefresh((value) => value + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let running = false;

    const load = async () => {
      if (running) return;
      running = true;

      try {
        const response =
          await CashEntryServices.getEntryDateWindow(
            key || undefined
          );

        const data = readCashData(response)?.dateWindow;

        if (
          !data?.min ||
          !data?.max ||
          !data?.defaultDateTime
        ) {
          throw new Error("Unable to load server date.");
        }

        if (!cancelled) {
          setState({
            key,
            dateWindow: data,
            dateWindowError: ""
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            key,
            dateWindow: null,
            dateWindowError: cashError(error)
          });
        }
      } finally {
        running = false;
      }
    };

    load();

    const timer = setInterval(load, 30000);

    const visible = () => {
      if (document.visibilityState === "visible") {
        load();
      }
    };

    document.addEventListener("visibilitychange", visible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener(
        "visibilitychange",
        visible
      );
    };
  }, [key, refresh]);

  return {
    dateWindow:
      state.key === key ? state.dateWindow : null,

    dateWindowError:
      state.key === key ? state.dateWindowError : "",

    reloadDateWindow: reload
  };
};